/**
 * Granular permissions for CRM Extended Reports sections.
 * Each section is a top-level CRM > Reports nav item gated by its own permission.
 * Keep in sync with services/crm-service/src/config/crmExtendedReportPermissions.js
 */

export const CRM_EXTENDED_REPORT_SECTIONS = [
  {
    category: 'Leads',
    navName: 'Leads',
    slug: 'leads',
    href: '/crm/reports/extended/leads',
    permission: 'CRM:EXTENDED_REPORTS_LEADS:READ',
    reportTypes: [
      'lead-register',
      'lead-pipeline',
      'lead-source-analysis',
      'lead-follow-up',
      'lead-lost',
      'lead-conversion',
    ],
  },
  {
    category: 'Applications',
    navName: 'Applications',
    slug: 'applications',
    href: '/crm/reports/extended/applications',
    permission: 'CRM:EXTENDED_REPORTS_APPLICATIONS:READ',
    reportTypes: [
      'application-register',
      'application-pending',
      'application-approved',
      'application-rejected',
      'eligibility-assessment',
      'fully-paid-applicants',
    ],
  },
  {
    category: 'Registrations',
    navName: 'Registrations',
    slug: 'registrations',
    href: '/crm/reports/extended/registrations',
    permission: 'CRM:EXTENDED_REPORTS_REGISTRATIONS:READ',
    reportTypes: [
      'registration-register',
      'application-enrollment-report',
      'registration-daily',
      'registration-monthly',
      'registration-status',
      'registration-trend',
      'center-self-registrations',
    ],
  },
  {
    // Standalone copy of the Registrations "Application + Enrollment" report with its own
    // permission and a Location filter (instead of the header-selected location).
    category: 'Application + Enrollment',
    navName: 'Application + Enrollment',
    slug: 'application-enrollment',
    href: '/crm/reports/extended/application-enrollment',
    permission: 'CRM:EXTENDED_REPORTS_APPLICATION_ENROLLMENT:READ',
    reportTypes: ['application-enrollment-all-locations'],
  },
  {
    category: 'Students',
    navName: 'Students',
    slug: 'students',
    href: '/crm/reports/extended/students',
    permission: 'CRM:EXTENDED_REPORTS_STUDENTS:READ',
    reportTypes: [
      'active-students',
      'completed-students',
      'deferred-students',
      'withdrawn-students',
      'student-demographics',
      'communication-history',
    ],
  },
  {
    category: 'Classes',
    navName: 'Classes',
    slug: 'classes',
    href: '/crm/reports/extended/classes',
    permission: 'CRM:EXTENDED_REPORTS_CLASSES:READ',
    reportTypes: ['class-allocation', 'class-capacity', 'class-utilization'],
  },
  {
    category: 'Financial',
    navName: 'Financial',
    slug: 'financial',
    href: '/crm/reports/extended/financial',
    permission: 'CRM:EXTENDED_REPORTS_FINANCIAL:READ',
    reportTypes: [
      'revenue-summary',
      'payment-plan-report',
      'program-revenue',
      'installment-report',
      'outstanding-payments',
      'full-payment-students',
      'revenue-by-intake',
      'revenue-by-executive',
      'refund-report',
      'center-settlement-aging',
    ],
  },
  {
    category: 'Performance',
    navName: 'Performance',
    slug: 'performance',
    href: '/crm/reports/extended/performance',
    permission: 'CRM:EXTENDED_REPORTS_PERFORMANCE:READ',
    reportTypes: [
      'individual-performance',
      'team-performance',
      'target-achievement',
      'revenue-contribution',
      'officer-activity',
      'ranking-report',
    ],
  },
  {
    category: 'Legacy Listings',
    navName: 'Legacy Listings',
    slug: 'legacy',
    href: '/crm/reports/extended/legacy',
    permission: 'CRM:EXTENDED_REPORTS_LEGACY:READ',
    reportTypes: [
      'product-registration-listing',
      'intake-wise-students',
      'student-payment-info',
      'student-status-register',
    ],
  },
];

export const CRM_EXTENDED_PARENT_PERMISSION = 'CRM:EXTENDED_REPORTS:READ';
export const CRM_EXTENDED_CONFIG_EXPORTS_PERMISSION = 'CRM:EXTENDED_REPORTS_CONFIG_EXPORTS:READ';
export const CRM_EXTENDED_CONFIG_EXPORTS_HREF = '/crm/reports/extended/exports';

export const CRM_EXTENDED_SECTION_PERMISSIONS = CRM_EXTENDED_REPORT_SECTIONS.map((s) => s.permission);

const sectionByCategory = new Map(CRM_EXTENDED_REPORT_SECTIONS.map((s) => [s.category, s]));
const sectionBySlug = new Map(CRM_EXTENDED_REPORT_SECTIONS.map((s) => [s.slug, s]));
const sectionByReportType = new Map(
  CRM_EXTENDED_REPORT_SECTIONS.flatMap((s) => s.reportTypes.map((key) => [key, s]))
);

export const getSectionBySlug = (slug) => sectionBySlug.get(slug) || null;

export const getExtendedReportCategory = (reportType) =>
  sectionByReportType.get(reportType)?.category || null;

export const getExtendedReportSectionPermission = (reportType) =>
  sectionByReportType.get(reportType)?.permission || null;

const checkPerm = (hasPermission, code) =>
  typeof hasPermission === 'function' && hasPermission(code);

export const hasExtendedReportSectionAccess = (hasPermission, category) => {
  const section = sectionByCategory.get(category);
  if (!section) return false;
  return checkPerm(hasPermission, section.permission);
};

export const hasExtendedReportTypeAccess = (hasPermission, reportType) => {
  const category = getExtendedReportCategory(reportType);
  if (!category) return false;
  return hasExtendedReportSectionAccess(hasPermission, category);
};

export const getAllowedExtendedReportCategories = (hasPermission) =>
  CRM_EXTENDED_REPORT_SECTIONS
    .map((s) => s.category)
    .filter((category) => hasExtendedReportSectionAccess(hasPermission, category));

export const hasAnyExtendedReportSectionAccess = (hasPermission) =>
  getAllowedExtendedReportCategories(hasPermission).length > 0;

/** Backend portal OR-list: parent + all section codes (API route gating). */
export const EXTENDED_REPORTS_PORTAL_PERMISSIONS = [
  CRM_EXTENDED_PARENT_PERMISSION,
  ...CRM_EXTENDED_SECTION_PERMISSIONS,
];

export const canAccessExtendedReportsPortal = (hasPermission) =>
  EXTENDED_REPORTS_PORTAL_PERMISSIONS.some((code) => checkPerm(hasPermission, code));
